import React, {useMemo} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component"
import {SurveyStep, SurveyStepElement} from "models"
import AdminShowActions from "components/admin/show-actions"
import AttributeRow from "@kaspernj/api-maker/build/bootstrap/attribute-row"
import AttributeRows from "@kaspernj/api-maker/build/bootstrap/attribute-rows"
import Button from "components/inputs/button"
import inflection from "inflection"
import Locales from "shared/locales"
import memo from "set-state-compare/build/memo"
import Routes from "shared/routes"
import SurveyLink from "components/surveys/link"
import SurveyStepElementsTable from "components/survey-step-elements/table"
import {useAdminLayout} from "components/admin/layout"
import useCanCan from "@kaspernj/api-maker/build/use-can-can"
import useI18n from "i18n-on-steroids/build/src/use-i18n.js"
import useModel from "@kaspernj/api-maker/build/use-model"
import {useParams} from "@kaspernj/api-maker/build/router/route"
import {View} from "react-native"

const selectedSurveyStepAttributes = ["copyable", "createdAt", "id", "liquidConditions", "name", "position", "surveyStepElementsCount", "updatedAt"]

for (const locale of Locales.availableLocales()) {
  selectedSurveyStepAttributes.push(`name${locale.substring(0, 1).toUpperCase()}${locale.substring(1, 99)}`)
}

/** @typedef {object} RoutesAdminSurveyStepsShowProps */

/** @typedef {object} RoutesAdminSurveyStepsShowState */

/**
 * @extends {ShapeComponent<RoutesAdminSurveyStepsShowProps, RoutesAdminSurveyStepsShowState>}
 */
class RoutesAdminSurveyStepsShow extends ShapeComponent {
  setup() {
    const {t} = useI18n({namespace: "js.routes.admin.survey_steps.show"})
    const params = useParams()
    const adminLayout = useAdminLayout()
    const canCan = useCanCan(() => [[SurveyStepElement, ["create"]]])
    const {surveyStep, surveyStepId} = useModel(SurveyStep, {
      abilities: {
        SurveyStep: ["destroy", "edit"]
      },
      match: {params},
      preload: ["survey"],
      select: {
        Survey: ["id", "name"],
        SurveyStep: selectedSurveyStepAttributes
      }
    })

    this.canCan = canCan
    this.surveyStep = surveyStep
    this.surveyStepId = surveyStepId
    this.surveyStepElementsQuery = useMemo(
      () => SurveyStepElement.ransack({
        step_id_eq: surveyStepId
      }),
      [surveyStepId]
    )
    this.t = t

    useMemo(() => {
      if (!canCan) return

      adminLayout?.setState({
        actions: this.actions(),
        active: "surveys",
        headerTitle: surveyStep?.name()
      })
    }, [adminLayout, canCan.getCacheKey(), surveyStep?.id(), surveyStep?.name(), surveyStepId])
  }

  render() {
    const {surveyStepElementsQuery, surveyStep} = this.tt

    return (
      <View dataSet={{route: "admin--survey-steps--show"}}>
        {surveyStep &&
          <>
            <AttributeRows attributes={["id", "position", "surveyStepElementsCount", "copyable", "createdAt", "updatedAt"]} model={surveyStep} />
            <AttributeRow attribute="survey" model={surveyStep}>
              {surveyStep.survey() &&
                <SurveyLink adminLayout survey={surveyStep.survey()} />
              }
            </AttributeRow>
            {Locales.availableLocales().map((locale) =>
              <AttributeRow
                attribute={`name${inflection.camelize(locale)}`}
                key={locale}
                label={`${SurveyStep.humanAttributeName("name")} (${Locales.labelForLocale(locale)})`}
                model={surveyStep}
              />
            )}
            <AttributeRow attribute="liquidConditions" model={surveyStep}>
              <pre>
                {surveyStep.liquidConditions()}
              </pre>
            </AttributeRow>
          </>
        }
        <SurveyStepElementsTable adminLayout className="mt-4" collection={surveyStepElementsQuery} />
      </View>
    )
  }

  actions() {
    const {canCan, surveyStep, surveyStepId} = this.tt

    return (
      <>
        {canCan.can("create", SurveyStepElement) &&
          <Button
            className="new-element-button"
            icon="plus"
            label={this.t(".add_new_element")}
            to={Routes.newAdminSurveyStepElementPath({survey_step_element: {survey_step_id: surveyStepId}})}
          />
        }
        <AdminShowActions model={surveyStep} />
      </>
    )
  }
}

export default memo(shapeComponent(RoutesAdminSurveyStepsShow))
